import { useState } from 'react';
import type { ProfileInviteVM } from '@tv-casino/viewmodels/routes/profileModel.js';
import styles from './InviteCard.module.css';

/**
 * The profile's referral tracker: the link that brings another owner in, a
 * copy button, a share button where the browser has one, and the three
 * counts. Everything it prints comes from `profileModel`'s `invite`.
 */
export function InviteCard({ invite, className }: { invite: ProfileInviteVM; className?: string }) {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invite.link);
      setCopied(true);
    } catch {
      // No clipboard (an insecure LAN origin): the link is on screen to select.
      setCopied(false);
    }
  };

  const share = () => {
    navigator.share({ title: 'Run a casino night', url: invite.link }).catch(() => undefined);
  };

  return (
    <section className={className} aria-label="Invite owners">
      <h2 className={styles.title}>Invite owners</h2>
      <div className={styles.linkRow}>
        <input
          className={styles.link}
          value={invite.link}
          readOnly
          aria-label="Your invite link"
          onFocus={(e) => e.currentTarget.select()}
        />
        <button className={styles.button} type="button" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </button>
        {canShare ? (
          <button className={styles.button} type="button" onClick={share}>
            Share
          </button>
        ) : null}
      </div>
      <dl className={styles.counts}>
        {invite.stats.map((s) => (
          <div className={styles.count} key={s.label}>
            <dt className={styles.label}>{s.label}</dt>
            <dd className={styles.value}>{s.value}</dd>
          </div>
        ))}
      </dl>
      <p className={styles.note}>{invite.note}</p>
    </section>
  );
}
